import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { DevelopmentSummaryModel } from '@/domain/models/development.js';
import type { BusinessProcessResponse, ProjectUserResponse } from '@/domain/models/project.js';
import type { DevelopmentController } from '@/presentation/controllers/development-controller.js';
import type { ProjectController } from '@/presentation/controllers/project-controller.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import { useDevelopments } from '@/presentation/hooks/use-developments.js';
import { DevelopmentEstimateFields } from '@/presentation/pages/developments/components/development-estimate-fields.js';
import { ROUTES } from '@/shared/constants/routes.js';
import type { Complexity, DevelopmentCategory } from '@/shared/utils/format-status.js';

interface Props {
    controller: DevelopmentController;
    projectController: ProjectController;
}

const TITLE_MAX_LENGTH = 50;
const DESCRIPTION_MAX_LENGTH = 500;

export function DevelopmentFormPage({ controller, projectController }: Props) {
    const { translate, useAuthStore } = useApp();
    const navigate = useNavigate();
    const currentUser = useAuthStore((s) => s.user);
    const { selectedProject, createDevelopment } = useDevelopments(controller);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [businessProcessId, setBusinessProcessId] = useState('');
    const [businessProcesses, setBusinessProcesses] = useState<BusinessProcessResponse[]>([]);
    const [consultorId, setConsultorId] = useState('');
    const [consultores, setConsultores] = useState<ProjectUserResponse[]>([]);
    const [keyUserId, setKeyUserId] = useState('');
    const [keyUsers, setKeyUsers] = useState<ProjectUserResponse[]>([]);
    const [linkedDevelopment, setLinkedDevelopment] = useState<DevelopmentSummaryModel | null>(null);
    const [projectDevelopments, setProjectDevelopments] = useState<DevelopmentSummaryModel[]>([]);
    const [category, setCategory] = useState<DevelopmentCategory | ''>('');
    const [functionalComplexity, setFunctionalComplexity] = useState<Complexity | ''>('');
    const [functionalHours, setFunctionalHours] = useState('');
    const [estimatedDeliveryDate, setEstimatedDeliveryDate] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!selectedProject) {
            return;
        }
        projectController.listActiveBusinessProcesses(selectedProject.id).then((result) => {
            result.fold(
                () => setBusinessProcesses([]),
                (processes) => setBusinessProcesses(processes)
            );
        });
        projectController.listProjectUsers(selectedProject.id, 'CF').then((result) => {
            result.fold(
                () => setConsultores([]),
                (users) => {
                    setConsultores(users);
                    if (currentUser && users.some((u) => u.id === currentUser.id)) {
                        setConsultorId(currentUser.id);
                    }
                }
            );
        });
        projectController.listProjectUsers(selectedProject.id, 'UC').then((result) => {
            result.fold(
                () => setKeyUsers([]),
                (users) => setKeyUsers(users)
            );
        });
        controller.loadDevelopments({ projectId: selectedProject.id, limit: 100 }).then((result) => {
            result.fold(
                () => setProjectDevelopments([]),
                (data) => setProjectDevelopments(data.data)
            );
        });
    }, [projectController, controller, selectedProject, currentUser]);

    const idPreview = selectedProject ? `${selectedProject.abbreviation}-????` : '—';

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        if (!title.trim()) {
            setError(translate('devForm.titleRequired'));
            return;
        }
        if (!selectedProject) {
            setError(translate('devForm.projectRequired'));
            return;
        }
        if (!functionalComplexity) {
            setError(translate('devForm.functionalComplexityRequired'));
            return;
        }
        if (!category) {
            setError(translate('devForm.categoryRequired'));
            return;
        }
        const functionalHoursNumber = Number(functionalHours);
        if (!functionalHours || isNaN(functionalHoursNumber) || functionalHoursNumber < 1) {
            setError(translate('devForm.functionalHoursRequired'));
            return;
        }
        if (estimatedDeliveryDate && isNaN(new Date(estimatedDeliveryDate).getTime())) {
            setError(translate('devForm.estimatedDeliveryDateInvalid'));
            return;
        }
        setSaving(true);
        const result = await createDevelopment({
            title: title.trim(),
            description: description.trim(),
            businessProcessId: businessProcessId || undefined,
            projectId: selectedProject.id,
            consultorId: consultorId || undefined,
            usuarioChaveId: keyUserId || undefined,
            linkedDevelopmentId: linkedDevelopment?.id,
            functionalComplexity: functionalComplexity as Complexity,
            functionalHours: functionalHoursNumber,
            techComplexity: null,
            techHours: null,
            estimatedDeliveryDate: estimatedDeliveryDate || null,
            category: category as DevelopmentCategory
        });
        setSaving(false);
        result.fold(
            (err) => setError(err.message),
            (data) => {
                const url = ROUTES.DEVELOPMENT_DETAIL.replace(':id', data.id);
                if (data.jiraWarning) {
                    navigate(url, { state: { jiraWarning: data.jiraWarning } });
                    return;
                }
                navigate(url);
            }
        );
    };

    return (
        <Box sx={{ p: 3, maxWidth: 640, mx: 'auto' }}>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
                {translate('devForm.newDev')}
            </Typography>
            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField label={translate('devForm.fieldIdPreview')} value={idPreview} size="small" fullWidth disabled />
                <TextField
                    label={translate('devForm.fieldTitle')}
                    value={title}
                    onChange={(e) => setTitle(e.target.value.slice(0, TITLE_MAX_LENGTH))}
                    placeholder={translate('devForm.fieldTitlePlaceholder')}
                    size="small"
                    fullWidth
                    required
                    helperText={`${title.length}/${TITLE_MAX_LENGTH}`}
                    slotProps={{ formHelperText: { sx: { textAlign: 'right' } } }}
                />
                <TextField
                    label={translate('devForm.fieldDescription')}
                    value={description}
                    onChange={(e) => setDescription(e.target.value.slice(0, DESCRIPTION_MAX_LENGTH))}
                    placeholder={translate('devForm.fieldDescriptionPlaceholder')}
                    size="small"
                    multiline
                    rows={3}
                    fullWidth
                    helperText={`${description.length}/${DESCRIPTION_MAX_LENGTH}`}
                    slotProps={{ formHelperText: { sx: { textAlign: 'right' } } }}
                />
                <TextField
                    select
                    label={translate('devForm.fieldBusinessProcess')}
                    value={businessProcessId}
                    onChange={(e) => setBusinessProcessId(e.target.value)}
                    placeholder={translate('devForm.selectBusinessProcess')}
                    size="small"
                    fullWidth
                    helperText={businessProcesses.length === 0 ? translate('devForm.noBusinessProcessWarning') : undefined}
                >
                    {businessProcesses.map((bp) => (
                        <MenuItem key={bp.id} value={bp.id}>
                            {bp.name}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField select label={translate('devForm.fieldConsultor')} value={consultorId} onChange={(e) => setConsultorId(e.target.value)} size="small" fullWidth>
                    {consultores.map((u) => (
                        <MenuItem key={u.id} value={u.id}>
                            {u.name}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField select label={translate('devForm.fieldKeyUser')} value={keyUserId} onChange={(e) => setKeyUserId(e.target.value)} size="small" fullWidth>
                    <MenuItem value="">{translate('devForm.selectUser')}</MenuItem>
                    {keyUsers.map((u) => (
                        <MenuItem key={u.id} value={u.id}>
                            {u.name}
                        </MenuItem>
                    ))}
                </TextField>
                <Autocomplete
                    options={projectDevelopments}
                    value={linkedDevelopment}
                    onChange={(_, value) => setLinkedDevelopment(value)}
                    getOptionLabel={(d) => `${d.displayId} — ${d.title}`}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    size="small"
                    renderInput={(params) => <TextField {...params} label={translate('devForm.fieldLinkedDev')} placeholder={translate('devForm.linkedDevPlaceholder')} />}
                />
                <DevelopmentEstimateFields
                    translate={translate}
                    category={category}
                    onCategoryChange={setCategory}
                    functionalComplexity={functionalComplexity}
                    onFunctionalComplexityChange={setFunctionalComplexity}
                    functionalHours={functionalHours}
                    onFunctionalHoursChange={setFunctionalHours}
                    estimatedDeliveryDate={estimatedDeliveryDate}
                    onEstimatedDeliveryDateChange={setEstimatedDeliveryDate}
                />
                {error && (
                    <Typography color="error" variant="body2">
                        {error}
                    </Typography>
                )}
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button onClick={() => navigate(ROUTES.DEVELOPMENTS)}>{translate('devForm.cancel')}</Button>
                    <Button type="submit" variant="contained" disabled={saving} sx={{ color: 'white' }}>
                        {saving ? translate('devForm.saving') : translate('devForm.save')}
                    </Button>
                </Stack>
            </Box>
        </Box>
    );
}
